import React, { useCallback } from 'react';
import './SpellCard.css';
import { damageLinesColorStyle, getSpellLineIcon } from '../../core/weapon';
import Tooltip from '../utils/Tooltip';
import SpellDetails from './SpellDetails';
import MediaImg from '../utils/MediaImg';
import CriterionWeight from '../generator/CriterionWeight';
import { useSpellDetailsOverlay } from '../../hooks/useSpellDetailsOverlay';

// The equipped weapon's own criterion. Find-the-best-weapon is a card of its
// own (BestWeaponCard): it names no weapon, and the two are exclusive.
const TXT_MAXIMIZE = {
    fr: 'Maximiser cette arme',
    en: 'Maximise this weapon',
    de: 'Diese Waffe maximieren',
    es: 'Maximizar esta arma',
    pt: 'Maximizar esta arma',
};
const TXT_NO_WEAPON = {
    fr: 'Aucune arme équipée',
    en: 'No weapon equipped',
    de: 'Keine Waffe ausgerüstet',
    es: 'Ninguna arma equipada',
    pt: 'Nenhuma arma equipada',
};

const EMPTY_VALUE = '—';

const WeaponSpellCard = ({
    weapon,
    labels,
    lang = 'fr',
    selected = false,
    disabled = false,
    selectable = true,
    onToggleWeapon,
    showWeight = false,
    weight = 1,
    onWeightChange,
    suppressCriteriaHover = false,
}) => {
    const { onIconActivate, iconActivateLabel } = useSpellDetailsOverlay(weapon, labels, lang, 'weapon');
    // Row one scores the equipped weapon, so with an empty slot there is
    // nothing to select — it stays as a greyed placeholder rather than
    // collapsing, so the card keeps its height and the second row its place.
    const canToggle = selectable && !disabled && !!onToggleWeapon && !!weapon;
    // Both numbers price the same hit, so both wear the elements it lands in.
    const damageColor = damageLinesColorStyle(weapon?.baseLines);

    const handleIconClick = useCallback(
        (e) => {
            e.stopPropagation();
            onIconActivate?.();
        },
        [onIconActivate]
    );

    const handleIconKeyDown = useCallback(
        (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            e.stopPropagation();
            onIconActivate?.();
        },
        [onIconActivate]
    );

    const handleRowClick = useCallback(
        (e) => {
            e.preventDefault();
            if (!canToggle) return;
            onToggleWeapon();
        },
        [canToggle, onToggleWeapon]
    );

    const handleRowKeyDown = useCallback(
        (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            if (!canToggle) return;
            onToggleWeapon();
        },
        [canToggle, onToggleWeapon]
    );

    const rootClassName = [
        'spell-card',
        disabled ? 'spell-card--disabled' : '',
        !selectable ? 'spell-card--unselectable' : '',
        suppressCriteriaHover ? 'spell-card--no-criteria-hover' : '',
    ].filter(Boolean).join(' ');

    const weightCell = (isSelected) => (showWeight ? (
        <div className="spell-view-weight-cell">
            {isSelected ? (
                <CriterionWeight
                    value={weight}
                    onChange={onWeightChange}
                    disabled={disabled || !selectable}
                    lang={lang}
                />
            ) : null}
        </div>
    ) : null);

    const rowClass = (isSelected, canPress, extra = '') => [
        'spell-view-row',
        canPress ? 'spell-view-row--selectable' : '',
        isSelected ? 'spell-view-row--selected' : '',
        extra,
    ].filter(Boolean).join(' ');

    const body = (
        <div className="spell-card-trigger">
            <div className={`spell-view${selectable ? '' : ' spell-view--readonly'}`}>
                <div className="spell-view-compact-icon">
                    {(() => {
                        const img = weapon?.icon ? (
                            <MediaImg className="spell-view-icon" src={weapon.icon} alt={weapon.name} loading="lazy" />
                        ) : (
                            <div className="spell-view-icon spell-view-icon-fallback" aria-hidden="true" />
                        );
                        if (!onIconActivate || !weapon) return img;
                        return (
                            <span
                                className="spell-view-icon-trigger"
                                role="button"
                                tabIndex={0}
                                aria-label={iconActivateLabel || weapon.name}
                                onClick={handleIconClick}
                                onKeyDown={handleIconKeyDown}
                            >
                                {img}
                            </span>
                        );
                    })()}
                </div>
                <div className="spell-view-compact-rows">
                    <div
                        className={rowClass(
                            selected,
                            canToggle,
                            `spell-view-row-first${weapon ? '' : ' spell-view-row--vacant'}`,
                        )}
                        role={canToggle ? 'button' : undefined}
                        tabIndex={canToggle ? 0 : undefined}
                        aria-pressed={canToggle ? selected : undefined}
                        title={weapon ? (TXT_MAXIMIZE[lang] || TXT_MAXIMIZE.en) : undefined}
                        onClick={canToggle ? handleRowClick : undefined}
                        onKeyDown={canToggle ? handleRowKeyDown : undefined}
                    >
                        <div className="spell-view-name">
                            {weapon ? weapon.name : (TXT_NO_WEAPON[lang] || TXT_NO_WEAPON.en)}
                        </div>
                        <div className="spell-view-val-cell">
                            <span className="spell-view-val" style={damageColor}>
                                {weapon ? weapon.cardPrimaryTotal : EMPTY_VALUE}
                            </span>
                            <div className="spell-view-el-icons" aria-hidden="true">
                                {(weapon?.baseLines || []).map((line, idx) => {
                                    const src = getSpellLineIcon(line);
                                    return src ? (
                                        <img key={`weapon-el-${line.el}-${idx}`} src={src} alt="" className="spell-view-el-icon" />
                                    ) : (
                                        <span key={`weapon-el-fallback-${line.el}-${idx}`} className="spell-view-el-fallback">
                                            {labels[line.el] || line.el}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                        {weightCell(selected)}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={rootClassName}>
            {weapon ? (
                <Tooltip
                    content={<SpellDetails spell={weapon} labels={labels} lang={lang} />}
                    className="spell-card-detail-tooltip"
                    positionMode="side"
                    positionOptions={{ estimatedWidth: 280, useRightEdge: true }}
                >
                    {body}
                </Tooltip>
            ) : body}
        </div>
    );
};

export default WeaponSpellCard;
